<template>
    <div>
        <ts-page-title
            :title="$t('bankProfile.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('bankProfile.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <div class="tw-flex tw-items-center tw-gap-3">
                    <Button
                        type="primary"
                        icon="ios-add"
                        @click="$router.push({ name: 'bank-profile-create' })"
                    >
                        {{ $t("addNew") }}
                    </Button>
                    <span class="tw-text-sm tw-text-gray-500">
                        {{ $tc('bankProfile.bankCount', resources.length, { count: resources.length }) }}
                    </span>
                </div>
                <Input
                    search
                    v-model="search"
                    :placeholder="$t('bankProfile.search')"
                    style="width: 300px"
                />
            </ts-panel-wrapper>

            <Table
                ref="table"
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                stripe
                :highlight-row="false"
                :row-class-name="rowClassName"
            >
                <template slot-scope="{ row }" slot="bank">
                    <div class="tw-flex tw-items-center tw-gap-3 tw-py-1">
                        <div
                            class="tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-semibold tw-flex-shrink-0"
                            :style="{ background: avatarColor(row.bank_name) }"
                        >
                            {{ initial(row.bank_name) }}
                        </div>
                        <div>
                            <div class="tw-font-medium tw-text-gray-800">{{ row.bank_name }}</div>
                            <div class="tw-text-xs tw-text-gray-400">ID: {{ row.bank_id }}</div>
                        </div>
                    </div>
                </template>

                <template slot-scope="{ row }" slot="accounts">
                    <a
                        v-if="row.accounts && row.accounts.length"
                        href="#"
                        class="ivu-tag ivu-tag-size-default tw-cursor-pointer"
                        :class="row._expanded ? 'ivu-tag-primary' : 'ivu-tag-default'"
                        @click.prevent="toggleExpand(row)"
                    >
                        <i class="fas fa-users tw-mr-1" style="font-size:10px;"></i>
                        {{ row.accounts.length }} {{ $t('bankProfile.accounts') }}
                    </a>
                    <a
                        v-else
                        href="#"
                        class="tw-text-xs tw-text-gray-400 hover:tw-text-blue-500"
                        @click.prevent="onAddAccount(row)"
                    >
                        <i class="fas fa-plus-circle tw-mr-1"></i>{{ $t('bankProfile.noAccountsYet') }}
                    </a>
                </template>

                <template slot-scope="{ row }" slot="action">
                    <div class="tw-flex tw-items-center tw-justify-center tw-gap-1">
                        <Button
                            type="primary"
                            size="small"
                            ghost
                            @click="onAddAccount(row)"
                            v-tooltip="$t('bankProfile.addAccount')"
                        >
                            <i class="fas fa-plus"></i>
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            @click="onEdit(row)"
                            v-tooltip="$t('bankProfile.manageAccounts')"
                        >
                            <i class="fas fa-cog"></i>
                        </Button>
                        <Poptip
                            :title="$t('are_you_sure_to_delete')"
                            :transfer="true"
                            width="220"
                            placement="left"
                        >
                            <Button
                                type="error"
                                size="small"
                                ghost
                                :disabled="row._deleting"
                                v-tooltip="$t('delete')"
                            >
                                <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                                <i class="fas fa-trash" v-else></i>
                            </Button>
                            <div slot="content" class="tw-text-right tw-mt-2">
                                <a
                                    class="ivu-btn ivu-btn-default ivu-btn-small tw-mr-2"
                                >
                                    {{ $t("cancel") }}
                                </a>
                                <a
                                    class="ivu-btn ivu-btn-error ivu-btn-small"
                                    @click.prevent="onDelete(row)"
                                >
                                    <span>{{ $t("yes") }}</span>
                                </a>
                            </div>
                        </Poptip>
                    </div>
                </template>
            </Table>

            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
        </ts-panel>

        <account-form-modal
            v-if="addAccountBank"
            v-model="accountModal"
            :bank-id="addAccountBank.bank_id"
            :bank-name="addAccountBank.bank_name"
            :account="null"
            @saved="onAccountSaved"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce } from "lodash";
import expandRow from "./table-expand.vue";
import AccountFormModal from "./components/account-form-modal.vue";

const AVATAR_COLORS = [
    "#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6",
    "#1abc9c", "#e67e22", "#34495e", "#16a085", "#2980b9"
];

export default {
    name: "BankProfileIndex",
    components: { AccountFormModal },
    data() {
        return {
            loading: false,
            accountModal: false,
            addAccountBank: null,
            recentlyChangedId: null
        };
    },
    computed: {
        ...mapState("finance/bankProfile", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.finance.bankProfile.search;
            },
            set(value) {
                this.$store.commit("finance/bankProfile/SET_SEARCH", value);
                this.$store.commit("finance/bankProfile/RESET_CURRENT_PAGE");
            }
        },
        columns() {
            return [
                {
                    type: "expand",
                    width: 44,
                    render: (h, params) => {
                        return h(expandRow, { props: { row: params.row } });
                    }
                },
                {
                    title: this.$t("bankProfile.bankName"),
                    slot: "bank",
                    minWidth: 260
                },
                {
                    title: this.$t("bankProfile.accounts"),
                    slot: "accounts",
                    minWidth: 160
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    align: "center",
                    width: 160
                }
            ];
        }
    },
    methods: {
        initial(name) {
            return (name || "?").trim().charAt(0).toUpperCase();
        },
        avatarColor(name) {
            const s = name || "";
            let hash = 0;
            for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
            return AVATAR_COLORS[hash % AVATAR_COLORS.length];
        },
        rowClassName(row) {
            return this.recentlyChangedId === row.bank_id ? "tw-bg-blue-50" : "";
        },
        toggleExpand(row) {
            if (this.$refs.table && this.$refs.table.toggleExpand) {
                this.$refs.table.toggleExpand(row._index !== undefined ? row._index : this.resources.indexOf(row));
                this.$set(row, "_expanded", !row._expanded);
            }
        },
        highlight(bankId) {
            this.recentlyChangedId = bankId;
            setTimeout(() => {
                if (this.recentlyChangedId === bankId) this.recentlyChangedId = null;
            }, 2000);
        },
        fetchData(attributes = {}) {
            this.loading = true;
            this.$store
                .dispatch("finance/bankProfile/fetch", {
                    ...attributes,
                    filter: { search: this.search }
                })
                .finally(() => (this.loading = false));
        },
        onEdit(row) {
            this.$store.commit("finance/bankProfile/SET_EDIT_DATA", row);
            this.$router.push({
                name: "bank-profile-edit",
                params: { id: row.bank_id }
            });
        },
        onDelete(row) {
            this.$set(row, "_deleting", true);
            this.$store
                .dispatch("finance/bankProfile/destroy", row.bank_id)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.fetchData();
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => this.$set(row, "_deleting", false));
        },
        onAddAccount(row) {
            this.addAccountBank = row;
            this.accountModal = true;
        },
        onAccountSaved({ response }) {
            this.notice({
                type: response && response.message_id == 0 ? "success" : "warning",
                text: (response && response.message) || this.$t("saved")
            });
            if (this.addAccountBank) this.highlight(this.addAccountBank.bank_id);
            this.fetchData();
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "BANK PROFILE",
                desc: not.text
            });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.fetchData());
    }
};
</script>

<style scoped>
::v-deep .ivu-table-row-highlight td {
    background-color: #eff6ff !important;
}
</style>
